import { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { sanitizeHtml } from '../../lib/sanitize';
import { useConfirm } from '../ui/ConfirmDialog';
import { Button } from '../ui/Button';
import {
  Mail,
  Users,
  Send,
  Save,
  TestTube,
  Trash2,
  History,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Image as ImageIcon,
  Link as LinkIcon,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Heading3,
  Type,
  Minus,
  Palette,
  Link2Off,
  ImagePlus,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ImageGallery from './ImageGallery';
import EmailHistory from './EmailHistory';

interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
}

interface EmailTemplate {
  id: string;
  nombre: string;
  asunto: string;
  contenido_html: string;
  es_favorita: boolean;
  creado_en: string;
}

export default function EmailComposer() {
  const confirm = useConfirm();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [subject, setSubject] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'resizable-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-cubus-lime underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      TextStyle,
      Underline,
    ],
    content: '<p>Escribe tu mensaje aquí...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] p-4',
      },
    },
  });

  useEffect(() => {
    loadProfiles();
    loadTemplates();
  }, []);

  const loadProfiles = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, avatar_url')
      .eq('active', true)
      .order('full_name');

    if (!error && data) {
      setProfiles(data);
    }
  };

  const loadTemplates = async () => {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('es_favorita', { ascending: false })
      .order('creado_en', { ascending: false });

    if (!error && data) {
      setTemplates(data);
    }
  };

  const filteredProfiles = profiles.filter((profile) => {
    const matchesSearch =
      profile.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      profile.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || profile.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAll = () => {
    setSelectedUsers(filteredProfiles.map((p) => p.id));
  };

  const deselectAll = () => {
    setSelectedUsers([]);
  };

  const loadTemplate = (template: EmailTemplate) => {
    setSubject(template.asunto);
    editor?.commands.setContent(template.contenido_html);
    showNotification('success', 'Plantilla cargada correctamente');
  };

  const saveAsTemplate = async () => {
    const templateName = prompt('Nombre de la plantilla:');
    if (!templateName || !editor) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      showNotification('error', 'Debes iniciar sesión');
      return;
    }

    const { error } = await supabase.from('email_templates').insert({
      nombre: templateName,
      asunto: subject,
      contenido_html: sanitizeHtml(editor.getHTML()),
      es_favorita: false,
      creado_por: user.id,
    });

    if (error) {
      console.error('Error al guardar plantilla:', error);
      showNotification('error', 'Error al guardar plantilla: ' + error.message);
    } else {
      showNotification('success', 'Plantilla guardada');
      loadTemplates();
    }
  };

  const deleteTemplate = async (templateId: string) => {
    const ok = await confirm({
      title: 'Eliminar plantilla',
      description: '¿Seguro que deseas eliminar esta plantilla de correo?',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    const { error } = await supabase.from('email_templates').delete().eq('id', templateId);

    if (!error) {
      showNotification('success', 'Plantilla eliminada');
      loadTemplates();
    }
  };

  const sendEmail = async () => {
    if (selectedUsers.length === 0) {
      showNotification('error', 'Selecciona al menos un destinatario');
      return;
    }

    if (!subject.trim()) {
      showNotification('error', 'El asunto no puede estar vacío');
      return;
    }

    if (!editor || editor.isEmpty) {
      showNotification('error', 'El contenido no puede estar vacío');
      return;
    }

    const confirmed = await confirm({
      title: 'Enviar correo',
      description: `Se enviará este correo a ${selectedUsers.length} usuario${selectedUsers.length !== 1 ? 's' : ''}.`,
      confirmText: 'Enviar',
      tone: 'info',
    });
    if (!confirmed) {
      return;
    }

    setIsSending(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-bulk-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            destinatarios: selectedUsers,
            asunto: subject,
            contenido_html: sanitizeHtml(editor.getHTML()),
            incluir_firma: includeSignature,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        showNotification(
          'success',
          `Correo enviado exitosamente a ${result.successCount} de ${result.totalRecipients} destinatarios`
        );
        clearAll();
      } else {
        showNotification('error', result.error || 'Error al enviar correo');
      }
    } catch (error) {
      showNotification('error', 'Error de conexión al enviar correo');
    } finally {
      setIsSending(false);
    }
  };

  const sendTest = async () => {
    if (!subject.trim() || !editor || editor.isEmpty) {
      showNotification('error', 'Completa el asunto y contenido');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setIsSending(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-bulk-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            destinatarios: [user.id],
            asunto: `[PRUEBA] ${subject}`,
            contenido_html: sanitizeHtml(editor.getHTML()),
            incluir_firma: includeSignature,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        showNotification('success', 'Correo de prueba enviado a tu email');
      } else {
        showNotification('error', result.error || 'Error al enviar prueba');
      }
    } catch (error) {
      showNotification('error', 'Error de conexión');
    } finally {
      setIsSending(false);
    }
  };

  const clearAll = () => {
    setSubject('');
    editor?.commands.setContent('<p>Escribe tu mensaje aquí...</p>');
    setSelectedUsers([]);
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const insertImage = (url: string) => {
    const width = prompt('Ancho de la imagen (en pixeles, ej: 400):', '400');
    if (width) {
      editor?.chain().focus().setImage({
        src: url,
        style: `width: ${width}px; height: auto; max-width: 100%; cursor: pointer;`,
      }).run();
    }
    setShowImageGallery(false);
  };

  const insertImageFromURL = () => {
    const url = prompt('URL de la imagen:');
    if (url) {
      const width = prompt('Ancho de la imagen (en pixeles, ej: 400):', '400');
      if (width) {
        editor?.chain().focus().setImage({
          src: url,
          style: `width: ${width}px; height: auto; max-width: 100%; cursor: pointer;`,
        }).run();
      }
    }
  };

  const insertLink = () => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = prompt('URL del enlace:', previousUrl || '');

    if (url === null) {
      return;
    }

    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const setTextColor = () => {
    const color = prompt('Color del texto (formato hex, ej: #FF0000):', '#000000');
    if (color) {
      editor?.chain().focus().setColor(color).run();
    }
  };

  const removeTextColor = () => {
    editor?.chain().focus().unsetColor().run();
  };

  if (!editor) return null;

  return (
    <>
      <style>{`
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          cursor: pointer;
          border: 2px solid transparent;
          transition: border-color 0.2s;
        }
        .ProseMirror img:hover {
          border-color: #DDFF00;
        }
        .ProseMirror img.ProseMirror-selectednode {
          border-color: #DDFF00;
          outline: none;
        }
      `}</style>
      <div className="flex flex-1 gap-4 p-4 sm:p-6 lg:p-8">
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl border backdrop-blur-md shadow-lg ${
              notification.type === 'success'
                ? 'glass-surface border-emerald-400/30 text-emerald-200'
                : 'glass-surface border-rose-400/30 text-rose-200'
            }`}
          >
            {notification.message}
          </div>
        )}

      <div className="w-1/4 glass-surface rounded-2xl border border-white/[0.06] p-4 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-cubus-lime" />
          <h3 className="font-semibold text-white">Destinatarios</h3>
        </div>

        <div className="space-y-3 mb-4">
          <input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 glass-deep border border-white/[0.08] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime"
          />

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full px-3 py-2 glass-deep border border-white/[0.08] rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
          >
            <option value="all">Todos los roles</option>
            <option value="alumno">Solo Alumnos</option>
            <option value="docente">Solo Docentes</option>
            <option value="admin">Solo Admins</option>
          </select>

          <div className="flex gap-2">
            <Button variant="lime" size="sm" fullWidth onClick={selectAll}>
              Todos
            </Button>
            <Button variant="glass" size="sm" fullWidth onClick={deselectAll}>
              Ninguno
            </Button>
          </div>
        </div>

        <div className="mb-4 p-2 glass-deep border border-white/[0.06] rounded-lg text-center">
          <span className="text-cubus-lime font-semibold">{selectedUsers.length}</span>
          <span className="text-muted text-sm"> seleccionados</span>
        </div>

        <div className="space-y-2">
          {filteredProfiles.map((profile) => (
            <div
              key={profile.id}
              onClick={() => toggleUserSelection(profile.id)}
              className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                selectedUsers.includes(profile.id)
                  ? 'bg-cubus-lime/15 border-cubus-lime/60'
                  : 'glass-deep border-white/[0.06] hover:border-white/[0.12]'
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(profile.id)}
                  onChange={() => {}}
                  className="w-4 h-4 accent-cubus-lime"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {profile.full_name || 'Sin nombre'}
                  </p>
                  <p className="text-muted text-xs truncate">{profile.email}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 glass-surface rounded-2xl border border-white/[0.06] p-6 flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-cubus-lime" />
          <h3 className="font-semibold text-white">Redactar Correo</h3>
        </div>

        <input
          type="text"
          placeholder="Asunto del correo"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white placeholder-gray-500 mb-4 focus:outline-none focus:ring-2 focus:ring-cubus-lime"
        />

        <div className="mb-3 flex flex-wrap gap-1 p-2 glass-deep border border-white/[0.06] rounded-lg">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('bold') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Negrita"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('italic') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Cursiva"
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('underline') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Subrayado"
          >
            <UnderlineIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('strike') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Tachado"
          >
            <Strikethrough className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('heading', { level: 1 }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Encabezado 1"
          >
            <Heading1 className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('heading', { level: 2 }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Encabezado 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('heading', { level: 3 }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Encabezado 3"
          >
            <Heading3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('paragraph') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Párrafo"
          >
            <Type className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Alinear izquierda"
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Centrar"
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Alinear derecha"
          >
            <AlignRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive({ textAlign: 'justify' }) ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Justificar"
          >
            <AlignJustify className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('bulletList') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Lista con viñetas"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('orderedList') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Lista numerada"
          >
            <ListOrdered className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={() => setShowImageGallery(true)}
            className="p-2 rounded hover:bg-white/[0.06] text-white"
            title="Galería de imágenes"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <button
            onClick={insertImageFromURL}
            className="p-2 rounded hover:bg-white/[0.06] text-white"
            title="Insertar imagen desde URL"
          >
            <ImagePlus className="w-4 h-4" />
          </button>
          <button
            onClick={insertLink}
            className={`p-2 rounded hover:bg-white/[0.06] ${
              editor.isActive('link') ? 'bg-cubus-lime text-black' : 'text-white'
            }`}
            title="Insertar/editar enlace"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editor.isActive('link')}
            className="p-2 rounded hover:bg-white/[0.06] text-white disabled:opacity-30"
            title="Remover enlace"
          >
            <Link2Off className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="p-2 rounded hover:bg-white/[0.06] text-white"
            title="Separador horizontal"
          >
            <Minus className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={setTextColor}
            className="p-2 rounded hover:bg-white/[0.06] text-white"
            title="Color del texto"
          >
            <Palette className="w-4 h-4" />
          </button>
          <button
            onClick={removeTextColor}
            className="p-2 rounded hover:bg-white/[0.06] text-white"
            title="Remover color del texto"
          >
            <Type className="w-4 h-4" />
          </button>

          <div className="w-px bg-white/10 mx-1"></div>

          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="p-2 rounded hover:bg-white/[0.06] text-white disabled:opacity-30"
            title="Deshacer"
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="p-2 rounded hover:bg-white/[0.06] text-white disabled:opacity-30"
            title="Rehacer"
          >
            <Redo className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 glass-deep border border-white/[0.06] rounded-lg overflow-y-auto mb-4">
          <EditorContent editor={editor} />
        </div>

        <label className="flex items-center gap-2 text-white/70">
          <input
            type="checkbox"
            checked={includeSignature}
            onChange={(e) => setIncludeSignature(e.target.checked)}
            className="w-4 h-4 accent-cubus-lime"
          />
          <span className="text-sm">Incluir firma automática</span>
        </label>
      </div>

      <div className="w-1/5 glass-surface rounded-2xl border border-white/[0.06] p-4 overflow-y-auto">
        <h3 className="font-semibold text-white mb-4">Plantillas</h3>

        <div className="space-y-2 mb-6 max-h-[300px] overflow-y-auto">
          {templates.map((template) => (
            <div
              key={template.id}
              className="p-3 glass-deep border border-white/[0.06] rounded-lg hover:border-white/[0.12] cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-white text-sm font-medium flex-1">{template.nombre}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTemplate(template.id);
                  }}
                  className="text-rose-400 hover:text-rose-300 ml-2"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <p className="text-muted text-xs mb-2">
                {new Date(template.creado_en).toLocaleDateString()}
              </p>
              <Button variant="lime" size="sm" fullWidth onClick={() => loadTemplate(template)}>
                Cargar
              </Button>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Button
            variant="lime"
            size="lg"
            fullWidth
            leftIcon={<Send className="w-4 h-4" />}
            onClick={sendEmail}
            loading={isSending}
          >
            {isSending ? 'Enviando...' : 'Enviar Ahora'}
          </Button>

          <Button
            variant="glass"
            fullWidth
            leftIcon={<Save className="w-4 h-4" />}
            onClick={saveAsTemplate}
          >
            Guardar Plantilla
          </Button>

          <Button
            variant="glass"
            fullWidth
            leftIcon={<TestTube className="w-4 h-4" />}
            onClick={sendTest}
            loading={isSending}
          >
            Enviar Prueba
          </Button>

          <Button
            variant="glass"
            fullWidth
            leftIcon={<Trash2 className="w-4 h-4" />}
            onClick={clearAll}
          >
            Limpiar Todo
          </Button>

          <Button
            variant="glass"
            fullWidth
            leftIcon={<History className="w-4 h-4" />}
            onClick={() => setShowHistory(true)}
          >
            Ver Historial
          </Button>
        </div>
      </div>

        {showImageGallery && (
          <ImageGallery
            onClose={() => setShowImageGallery(false)}
            onSelectImage={insertImage}
          />
        )}

        {showHistory && <EmailHistory onClose={() => setShowHistory(false)} />}
      </div>
    </>
  );
}
